import { useRef, useState } from "react";
import { toast } from "sonner";
import { FileSpreadsheet, Download, Upload, Loader2, ChevronDown, HandCoins } from "lucide-react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel } from "./ui/dropdown-menu";
import { api } from "../lib/api";
import { saveBlob } from "../lib/download";

const ImportReport = ({ report, onClose }) => (
  <Dialog open={!!report} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="sm:max-w-md font-body max-h-[80vh] overflow-y-auto" dir="rtl" data-testid="advance-import-report">
      <DialogHeader>
        <DialogTitle>تقرير الاستيراد</DialogTitle>
        <DialogDescription>{report?.headline} الأسطر التالية تم تجاهلها أو تحتاج مراجعة:</DialogDescription>
      </DialogHeader>
      <div className="space-y-1.5 text-sm">
        {(report?.unmatched || []).map((u) => (
          <div key={`u${u.row}`} data-testid={`advance-unmatched-${u.row}`} className="rounded-md bg-amber-50 border border-amber-200 px-3 py-1.5 text-amber-800">
            سطر {u.row}: رقم الهوية <span dir="ltr" className="font-mono">{u.nid}</span> غير مرتبط بأي موظف
          </div>
        ))}
        {(report?.invalid || []).map((u) => (
          <div key={`i${u.row}`} data-testid={`advance-invalid-${u.row}`} className="rounded-md bg-rose-50 border border-rose-200 px-3 py-1.5 text-rose-700">
            سطر {u.row}: {u.reason}
          </div>
        ))}
      </div>
    </DialogContent>
  </Dialog>
);

export const AdvancesExcelBar = ({ canEdit, onImported }) => {
  const advRef = useRef(null);
  const repRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);

  const run = async (fn) => {
    setBusy(true);
    try { await fn(); } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر تنفيذ العملية"); } finally { setBusy(false); }
  };
  const onFile = (kind) => (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    run(async () => {
      const r = kind === "adv" ? await api.importAdvances(f) : await api.importRepayments(f);
      const headline = kind === "adv" ? `تم استيراد ${r.added} سلفة.` : `تم تسجيل ${r.applied} سداد بإجمالي ${r.total}.`;
      toast.success(headline);
      if (r.unmatched.length || r.invalid.length) setReport({ ...r, headline });
      onImported?.();
    });
  };
  const dl = (fn, name) => run(async () => saveBlob(await fn(), name));

  return (
    <>
      <DropdownMenu dir="rtl">
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="gap-1.5 h-9 text-emerald-700 border-emerald-200" data-testid="advance-excel-menu" disabled={busy}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />} Excel <ChevronDown className="h-3.5 w-3.5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56 font-body">
          <DropdownMenuLabel className="text-xs text-slate-400">السلف</DropdownMenuLabel>
          <DropdownMenuItem data-testid="advance-template-btn" className="gap-2" onClick={() => dl(api.advancesTemplate, "قالب_السلف.xlsx")}>
            <FileSpreadsheet className="h-4 w-4 text-blue-600" /> تنزيل قالب السلف
          </DropdownMenuItem>
          {canEdit && (
            <DropdownMenuItem data-testid="advance-import-btn" className="gap-2" onClick={() => advRef.current?.click()}>
              <Upload className="h-4 w-4 text-emerald-600" /> استيراد السلف
            </DropdownMenuItem>
          )}
          <DropdownMenuItem data-testid="advance-export-btn" className="gap-2" onClick={() => dl(api.exportAdvances, "السلف.xlsx")}>
            <Download className="h-4 w-4 text-slate-600" /> تصدير السلف
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuLabel className="text-xs text-slate-400">السدادات</DropdownMenuLabel>
          <DropdownMenuItem data-testid="advance-rep-template-btn" className="gap-2" onClick={() => dl(api.repaymentsTemplate, "قالب_السدادات.xlsx")}>
            <FileSpreadsheet className="h-4 w-4 text-blue-600" /> تنزيل قالب السدادات
          </DropdownMenuItem>
          {canEdit && (
            <DropdownMenuItem data-testid="advance-rep-import-btn" className="gap-2" onClick={() => repRef.current?.click()}>
              <HandCoins className="h-4 w-4 text-emerald-600" /> استيراد السدادات
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <input ref={advRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={onFile("adv")} data-testid="advance-import-input" />
      <input ref={repRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={onFile("rep")} data-testid="advance-rep-import-input" />
      <ImportReport report={report} onClose={() => setReport(null)} />
    </>
  );
};
